import {useCallback} from 'react';
import {createSelector} from 'reselect';

import {Analytics} from '../../../../services/analytics';
import {useMemoizedSelector} from '../../../../state/hooks';
import {useViewAction} from '../../../../state/views/hooks';
import {update as updateRunSet} from '../../../../state/views/runSet/actions';
import type {
  Ref as RunSetRef,
  RunSetPart,
} from '../../../../state/views/runSet/types';
import {makePartSelector} from '../../../../state/views/selectors';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';

const baselineSettingsSelector = (runSetRef: RunSetRef) =>
  createSelector(makePartSelector(runSetRef), runSet => ({
    showMetricDeltas: runSet.runFeed.showMetricDeltas !== false,
    hasBaselineRun: Boolean(runSet.baselineRunId),
  }));

const setShowMetricDeltas =
  (showMetricDeltas: boolean) => (runSet: RunSetPart) => ({
    ...runSet,
    runFeed: {...runSet.runFeed, showMetricDeltas},
  });

type UseBaselineSettingsHook = {
  hasBaselineRun: boolean;
  setShowMetricDelta: (showMetricDeltas: boolean) => void;
  showMetricDeltas: boolean;
};
export function useBaselineSettings(): UseBaselineSettingsHook {
  const {runSetRefs} = usePanelsSharedRefsContext();
  // NOTE: We can safely pull the first one off in this hook, because it should
  //       only be called in the context of the workspaces view (not in reports
  //       or elsewhere), and in the workspaces view there is always exactly one
  //       runSetRef.
  const runSetRef = runSetRefs[0];

  const {showMetricDeltas, hasBaselineRun} = useMemoizedSelector(
    baselineSettingsSelector,
    runSetRef
  );

  const runSetUpdate = useViewAction(runSetRef, updateRunSet);

  const setShowMetricDelta = useCallback(
    (showMetricDeltas: boolean) => {
      Analytics.track('Baseline settings changed', {
        action: 'delta metric visibility toggled',
        value: showMetricDeltas ? 'on' : 'off',
        location: 'workspace',
      });
      runSetUpdate(setShowMetricDeltas(showMetricDeltas));
    },
    [runSetUpdate]
  );

  return {
    hasBaselineRun,
    setShowMetricDelta,
    showMetricDeltas,
  };
}
